import React, { useState, useEffect, useCallback } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ChatProvider } from "./context/ChatContext";
import { LanguageProvider } from "./context/LanguageContext";
import ChatPage from "./pages/ChatPage";
import AuthPage from "./pages/AuthPage";
import LandingPage from "./pages/LandingPage";
import PaywallPage from "./pages/PaywallPage";
import { getBackendUrl } from "./utils/backendUrl";

// Accept token from cross-domain redirect (beauly.club → app.beauly.club)
(function() {
  const params = new URLSearchParams(window.location.search);
  const t = params.get("token");
  const u = params.get("user");
  if (t && u) {
    localStorage.setItem("beauly_token", t);
    localStorage.setItem("beauly_user", u);
    window.history.replaceState({}, "", window.location.pathname);
    window.location.reload();
  }
})();

function AppContent() {
  const { token, user, setUser, credits, needsPayment, createCheckout, fetchCredits } = useAuth();
  const [showApp, setShowApp] = useState(false);
  const [showLanding, setShowLanding] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [showCamera, setShowCamera] = useState(false);

  // Check payment status on load & after Stripe redirect
  const checkPayment = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${getBackendUrl()}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        localStorage.setItem("beauly_user", JSON.stringify(data));
      }
    } catch (e) { /* ignore */ }
  }, [token, setUser]);

  const verifyPayment = useCallback(async (sessionId) => {
    if (!token || !sessionId) return;
    try {
      await fetch(`${getBackendUrl()}/payments/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId }),
      });
      await checkPayment();
      await fetchCredits();
    } catch (e) { /* ignore */ }
  }, [token, checkPayment, fetchCredits]);

  useEffect(() => {
    // After Stripe redirect
    const params = new URLSearchParams(window.location.search);
    if (params.get("paid") === "true" || params.get("payment") === "success") {
      const sessionId = params.get("session_id");
      verifyPayment(sessionId);
      setShowCamera(true);
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("payment") === "cancel" || params.get("paid") === "cancel") {
      window.history.replaceState({}, "", window.location.pathname);
    } else if (token && user && !user.has_paid) {
      checkPayment();
    }
  }, [token, user, checkPayment, verifyPayment]);

  const hostname = window.location.hostname;
  const isLandingDomain = hostname === "beauly.club" || hostname === "www.beauly.club";
  const isAppDomain = hostname === "app.beauly.club";

  // After registration (no credits) — don't auto-redirect, let PaywallPage handle it

  // beauly.club → if logged in, redirect to app with token
  if (isLandingDomain) {
    if (token && user) {
      window.location.href = `https://app.beauly.club?token=${encodeURIComponent(token)}&user=${encodeURIComponent(JSON.stringify(user))}`;
      return null;
    }
    if (showApp) {
      return <AuthPage onBack={() => setShowApp(false)} initialMode={authMode} />;
    }
    return <LandingPage onGetStarted={(mode) => { setAuthMode(mode || "login"); setShowApp(true); }} />;
  }

  // app.beauly.club → skip landing, go straight to auth/chat
  if (isAppDomain) {
    if (!token) {
      return <AuthPage onBack={() => { window.location.href = "https://beauly.club"; }} />;
    }
    if (user && user.has_paid === false && needsPayment) {
      return <PaywallPage />;
    }
    return (
      <ChatProvider>
        <ChatPage onLogoClick={() => { window.location.href = "https://beauly.club"; }} showCameraOnMount={showCamera} onCameraDone={() => setShowCamera(false)} />
      </ChatProvider>
    );
  }

  // localhost / dev
  if (showLanding) {
    return <LandingPage onGetStarted={(mode) => { setAuthMode(mode || "login"); setShowLanding(false); setShowApp(true); }} />;
  }

  if (!showApp && !token) {
    return <LandingPage onGetStarted={(mode) => { setAuthMode(mode || "login"); setShowApp(true); }} />;
  }

  if (!token) {
    return <AuthPage onBack={() => setShowApp(false)} initialMode={authMode} />;
  }

  // Paywall check
  if (user && user.has_paid === false && needsPayment) {
    return <PaywallPage />;
  }

  return (
    <ChatProvider>
      <ChatPage onLogoClick={() => setShowLanding(true)} showCameraOnMount={showCamera} onCameraDone={() => setShowCamera(false)} />
    </ChatProvider>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
